<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useFollowUpStatus } from 'dashboard/composables/useFollowUpStatus';
import { useAlert } from 'dashboard/composables';
import Modal from 'dashboard/components/Modal.vue';
import ButtonV4 from 'dashboard/components-next/button/Button.vue';
import FupCreateSessionForm from './FupCreateSessionForm.vue';

const { t: $t } = useI18n();
const {
  isLoading,
  error,
  actionLoading,
  hasFupSessions,
  activeSessions,
  availableTypes,
  createFup,
  pauseFup,
  resumeFup,
  cancelFup,
} = useFollowUpStatus();

// UI state
const isExpanded = ref(false);
const showCreateForm = ref(false);
const selectedTipoId = ref(null);
const variaveisForm = ref({});
const showCancelModal = ref(false);
const sessionToCancel = ref(null);

const firstSession = computed(() => activeSessions.value[0] || null);
const selectedTipo = computed(
  () => availableTypes.value.find(t => t.id === selectedTipoId.value) || null
);
const needsVariaveis = computed(
  () => (selectedTipo.value?.variaveis_disponiveis || ['nome']).length > 0
);

const hasProgress = computed(() => {
  const s = firstSession.value;
  return (
    s && s.etapa_atual != null && s.total_etapas != null && s.total_etapas > 0
  );
});

const progressPercent = computed(() => {
  if (!hasProgress.value) return 0;
  return Math.round(
    (firstSession.value.etapa_atual / firstSession.value.total_etapas) * 100
  );
});

const progressBarColor = computed(() => {
  if (!firstSession.value) return 'bg-n-slate-5';
  if (firstSession.value.status === 'pausado') return 'bg-n-amber-9';
  return 'bg-n-blue-9';
});

const statusText = computed(() => {
  const s = firstSession.value;
  if (!s) return '';
  if (s.status === 'ativo' && hasProgress.value) {
    return `${s.fup_tipo_nome} · ${s.etapa_atual} de ${s.total_etapas} enviadas`;
  }
  if (s.status === 'pausado' && hasProgress.value) {
    return `${s.fup_tipo_nome} · pausado · ${s.etapa_atual} de ${s.total_etapas} enviadas`;
  }
  return s.fup_tipo_nome;
});

function formatDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function resetForm() {
  showCreateForm.value = false;
  selectedTipoId.value = null;
  variaveisForm.value = {};
}

function toggleExpand() {
  if (isExpanded.value) {
    isExpanded.value = false;
    resetForm();
  } else {
    isExpanded.value = true;
    if (!hasFupSessions.value) {
      showCreateForm.value = true;
    }
  }
}

function openCancelModal(id) {
  sessionToCancel.value = id;
  showCancelModal.value = true;
}

function closeCancelModal() {
  showCancelModal.value = false;
  sessionToCancel.value = null;
}

async function confirmCancel() {
  if (!sessionToCancel.value) return;
  const result = await cancelFup(sessionToCancel.value);
  useAlert(
    $t(
      result.success
        ? 'CONVERSATION.FOLLOW_UP.ACTIONS.CANCEL_SUCCESS'
        : 'CONVERSATION.FOLLOW_UP.ACTIONS.CANCEL_ERROR'
    )
  );
  closeCancelModal();
}

function onSelectTipo(tipoId) {
  selectedTipoId.value = tipoId;
  const tipo = availableTypes.value.find(t => t.id === tipoId);
  if (!tipo) return;
  const form = {};
  (tipo.variaveis_disponiveis || ['nome']).forEach(v => {
    form[v] = '';
  });
  variaveisForm.value = form;
}

async function doCreate() {
  const result = await createFup(selectedTipoId.value, variaveisForm.value);
  if (result.success) {
    useAlert($t('CONVERSATION.FOLLOW_UP.ACTIONS.START_SUCCESS'));
    resetForm();
    isExpanded.value = false;
  } else {
    useAlert(result.error || $t('CONVERSATION.FOLLOW_UP.ACTIONS.START_ERROR'));
  }
}

async function onPause(id) {
  const result = await pauseFup(id);
  useAlert(
    $t(
      result.success
        ? 'CONVERSATION.FOLLOW_UP.ACTIONS.PAUSE_SUCCESS'
        : 'CONVERSATION.FOLLOW_UP.ACTIONS.PAUSE_ERROR'
    )
  );
}

async function onResume(id) {
  const result = await resumeFup(id);
  useAlert(
    $t(
      result.success
        ? 'CONVERSATION.FOLLOW_UP.ACTIONS.RESUME_SUCCESS'
        : 'CONVERSATION.FOLLOW_UP.ACTIONS.RESUME_ERROR'
    )
  );
}
</script>

<template>
  <div class="fup-banner">
    <!-- LOADING -->
    <div v-if="isLoading && !hasFupSessions" class="fup-loading">
      <span class="fup-loading-dot" />
      <span class="text-xs font-medium text-n-slate-11">
        {{ $t('CONVERSATION.FOLLOW_UP.LOADING') }}
      </span>
    </div>

    <!-- ERROR RAIL (replaces normal rail when error + no sessions) -->
    <div
      v-else-if="error && !hasFupSessions"
      class="fup-rail fup-rail--error"
      role="button"
      tabindex="0"
      :aria-expanded="isExpanded"
      :aria-label="
        isExpanded
          ? $t('CONVERSATION.FOLLOW_UP.BANNER.COLLAPSE_TOOLTIP')
          : $t('CONVERSATION.FOLLOW_UP.BANNER.EXPAND_TOOLTIP')
      "
      @click="toggleExpand"
      @keydown.enter="toggleExpand"
    >
      <fluent-icon
        icon="error-circle"
        :size="14"
        class="flex-shrink-0 text-n-ruby-11"
        aria-hidden="true"
      />
      <span class="fup-rail__text fup-rail__text--error">
        {{ $t('CONVERSATION.FOLLOW_UP.ERROR_LOADING') }}
      </span>
      <span class="ml-auto flex items-center flex-shrink-0">
        <fluent-icon
          icon="chevron-down"
          :size="12"
          class="fup-chevron"
          :class="{ 'fup-chevron--open': isExpanded }"
          aria-hidden="true"
        />
      </span>
    </div>

    <!-- INFORMATION RAIL (collapsed header) -->
    <div
      v-else
      class="fup-rail"
      :class="{
        'fup-rail--active': firstSession?.status === 'ativo',
        'fup-rail--paused': firstSession?.status === 'pausado',
      }"
      role="button"
      tabindex="0"
      :aria-expanded="isExpanded"
      :aria-label="
        isExpanded
          ? $t('CONVERSATION.FOLLOW_UP.BANNER.COLLAPSE_TOOLTIP')
          : $t('CONVERSATION.FOLLOW_UP.BANNER.EXPAND_TOOLTIP')
      "
      @click="toggleExpand"
      @keydown.enter="toggleExpand"
    >
      <!-- Status icon -->
      <span v-if="firstSession?.status === 'ativo'" class="fup-pulse" />
      <fluent-icon
        v-else
        :icon="firstSession ? 'pause' : 'mail-off'"
        :size="14"
        class="flex-shrink-0 text-n-slate-10"
        aria-hidden="true"
      />

      <!-- Natural language status text -->
      <span v-if="firstSession" class="fup-rail__text">
        {{ statusText }}
      </span>
      <span v-else class="fup-rail__text fup-rail__text--empty">
        {{ $t('CONVERSATION.FOLLOW_UP.BANNER.NO_FUP') }}
      </span>

      <!-- Right: CTA or chevron -->
      <span class="ml-auto flex items-center gap-2 flex-shrink-0">
        <span
          v-if="!firstSession && !isExpanded && availableTypes.length > 0"
          class="fup-cta"
        >
          {{ $t('CONVERSATION.FOLLOW_UP.BANNER.CREATE_CTA') }}
        </span>
        <fluent-icon
          icon="chevron-down"
          :size="12"
          class="fup-chevron"
          :class="{ 'fup-chevron--open': isExpanded }"
          aria-hidden="true"
        />
      </span>

      <!-- Progress bar at bottom edge -->
      <div v-if="hasProgress" class="fup-progress-track">
        <div
          class="fup-progress-fill"
          :class="progressBarColor"
          :style="{ width: `${progressPercent}%` }"
        />
      </div>
    </div>

    <!-- EXPANDED ERROR DETAIL -->
    <div
      v-if="error && !hasFupSessions"
      v-show="isExpanded"
      class="fup-panel"
      :class="{ 'fup-panel--visible': isExpanded }"
    >
      <div class="fup-error-detail">
        {{ error }}
      </div>
    </div>

    <!-- EXPANDED PANEL -->
    <div
      v-show="isExpanded && !(error && !hasFupSessions)"
      class="fup-panel"
      :class="{ 'fup-panel--visible': isExpanded }"
    >
      <!-- Session details -->
      <div v-if="firstSession" class="fup-detail">
        <!-- Session info -->
        <div class="fup-detail__meta">
          <span class="text-xs font-semibold text-n-slate-12">
            {{ firstSession.fup_tipo_nome }}
          </span>
          <span
            v-if="firstSession.proximo_envio && firstSession.status === 'ativo'"
            class="text-[11px] text-n-slate-10"
          >
            {{ $t('CONVERSATION.FOLLOW_UP.NEXT_MESSAGE.LABEL') }}
            {{ formatDate(firstSession.proximo_envio) }}
          </span>
          <span
            v-if="firstSession.status === 'pausado'"
            class="text-[11px] text-n-amber-11"
          >
            {{ $t('CONVERSATION.FOLLOW_UP.PAUSED_TEXT') }}
          </span>
        </div>

        <!-- Progress detail -->
        <div v-if="hasProgress" class="fup-detail__progress">
          <div
            class="flex items-center justify-between text-[10px] font-medium text-n-slate-11 mb-1"
          >
            <span>{{ $t('CONVERSATION.FOLLOW_UP.PROGRESS.TITLE') }}</span>
            <span>
              {{ progressPercent }}%
              {{ $t('CONVERSATION.FOLLOW_UP.PROGRESS.COMPLETED') }}
            </span>
          </div>
          <div class="fup-detail__bar">
            <div
              class="fup-detail__bar-fill"
              :class="progressBarColor"
              :style="{ width: `${progressPercent}%` }"
            />
          </div>
          <div class="mt-1.5 text-[10px] text-n-slate-10">
            {{ firstSession.etapa_atual }}
            {{ $t('CONVERSATION.FOLLOW_UP.PROGRESS.OF') }}
            {{ firstSession.total_etapas }}
            {{ $t('CONVERSATION.FOLLOW_UP.PROGRESS.MESSAGES_SENT') }}
          </div>
        </div>

        <!-- Actions -->
        <div class="fup-detail__actions">
          <button
            v-if="firstSession.status === 'ativo'"
            class="fup-action fup-action--secondary"
            :disabled="actionLoading"
            @click.stop="onPause(firstSession.id)"
          >
            <fluent-icon icon="pause" :size="14" aria-hidden="true" />
            {{ $t('CONVERSATION.FOLLOW_UP.ACTIONS.PAUSE') }}
          </button>
          <button
            v-if="firstSession.status === 'pausado'"
            class="fup-action fup-action--primary"
            :disabled="actionLoading"
            @click.stop="onResume(firstSession.id)"
          >
            <fluent-icon icon="play" :size="14" aria-hidden="true" />
            {{ $t('CONVERSATION.FOLLOW_UP.ACTIONS.RESUME') }}
          </button>
          <button
            class="fup-action fup-action--danger"
            :disabled="actionLoading"
            @click.stop="openCancelModal(firstSession.id)"
          >
            {{ $t('CONVERSATION.FOLLOW_UP.ACTIONS.CANCEL') }}
          </button>
        </div>
      </div>

      <!-- Create form -->
      <FupCreateSessionForm
        v-else-if="showCreateForm"
        :available-types="availableTypes"
        :selected-tipo-id="selectedTipoId"
        :selected-tipo="selectedTipo"
        :variaveis-form="variaveisForm"
        :needs-variaveis="needsVariaveis"
        :action-loading="actionLoading"
        @select-tipo="onSelectTipo"
        @do-create="doCreate"
        @reset-form="
          () => {
            resetForm();
            isExpanded = false;
          }
        "
        @update-variavel="
          ({ key, value }) => {
            variaveisForm[key] = value;
          }
        "
      />
    </div>

    <!-- Cancel modal -->
    <Modal v-model:show="showCancelModal" :on-close="closeCancelModal">
      <div class="h-auto overflow-auto flex flex-col">
        <woot-modal-header
          :header-title="
            $t('CONVERSATION.FOLLOW_UP.ACTIONS.CANCEL_MODAL.TITLE')
          "
          :header-content="
            $t('CONVERSATION.FOLLOW_UP.ACTIONS.CANCEL_MODAL.BODY')
          "
        />
        <div class="flex flex-row justify-end gap-2 py-4 px-6 w-full">
          <ButtonV4
            faded
            slate
            type="reset"
            :label="$t('CONVERSATION.FOLLOW_UP.ACTIONS.CANCEL_MODAL.BACK')"
            @click="closeCancelModal"
          />
          <ButtonV4
            type="submit"
            :label="$t('CONVERSATION.FOLLOW_UP.ACTIONS.CANCEL_MODAL.CONFIRM')"
            @click="confirmCancel"
          />
        </div>
      </div>
    </Modal>
  </div>
</template>

<style scoped>
/* Container */
.fup-banner {
  @apply border-b border-n-weak bg-n-background;
}

/* Loading */
.fup-loading {
  @apply flex items-center gap-2 px-4 py-2;
}
.fup-loading-dot {
  @apply w-2 h-2 rounded-full bg-n-blue-9 animate-pulse;
}

/* ═══ INFORMATION RAIL (collapsed) ═══ */
.fup-rail {
  @apply relative flex items-center gap-2.5 w-full px-4 py-2.5 min-h-[44px]
         cursor-default text-left select-none
         transition-colors duration-150
         hover:bg-n-slate-2 dark:hover:bg-n-solid-2;
}
.fup-rail--active {
  @apply bg-n-blue-1 dark:bg-n-blue-2/20
         hover:bg-n-blue-2 dark:hover:bg-n-blue-3/30;
}
.fup-rail--paused {
  @apply bg-n-amber-1 dark:bg-n-amber-2/20
         hover:bg-n-amber-2 dark:hover:bg-n-amber-3/30;
}
.fup-rail--error {
  @apply bg-n-ruby-1 dark:bg-n-ruby-2/20
         hover:bg-n-ruby-2 dark:hover:bg-n-ruby-3/30;
}

/* Animated pulse dot for active state */
.fup-pulse {
  @apply w-2.5 h-2.5 rounded-full bg-n-blue-9 flex-shrink-0;
  animation: fup-pulse-anim 2s ease-in-out infinite;
}
@keyframes fup-pulse-anim {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.4;
  }
}

/* Rail text */
.fup-rail__text {
  @apply text-xs font-medium text-n-slate-12 truncate;
}
.fup-rail__text--empty {
  @apply text-n-slate-10;
}
.fup-rail__text--error {
  @apply text-n-ruby-11;
}

/* Create CTA */
.fup-cta {
  @apply text-[11px] font-semibold text-n-blue-11 dark:text-n-blue-10
         px-2.5 py-1 rounded-md
         bg-n-blue-3 dark:bg-n-blue-3/40
         border border-n-blue-6
         transition-all duration-150;
}
.fup-rail:hover .fup-cta {
  @apply bg-n-blue-4 dark:bg-n-blue-4/40;
}

/* Chevron */
.fup-chevron {
  @apply text-n-slate-8 transition-transform duration-200;
}
.fup-chevron--open {
  @apply rotate-180;
}

/* Progress bar track (bottom edge of rail) */
.fup-progress-track {
  @apply absolute bottom-0 left-0 right-0 h-[2px] bg-n-slate-3 dark:bg-n-solid-2;
}
.fup-progress-fill {
  @apply h-full rounded-r-full transition-all duration-500 ease-out;
}

/* ═══ EXPANDED PANEL ═══ */
.fup-panel {
  @apply transition-all duration-200 opacity-0 max-h-0 overflow-hidden;
}
.fup-panel--visible {
  @apply opacity-100 max-h-[500px];
}

/* Detail card */
.fup-detail {
  @apply mx-4 mb-3 p-4 rounded-lg
         bg-white dark:bg-n-solid-1
         border border-n-weak;
}

.fup-detail__meta {
  @apply flex flex-col gap-1 mb-3;
}

.fup-detail__progress {
  @apply mb-3 pb-3 border-b border-n-weak;
}

.fup-detail__bar {
  @apply h-2 bg-n-slate-3 dark:bg-n-solid-2 rounded-full overflow-hidden;
}
.fup-detail__bar-fill {
  @apply h-full rounded-full transition-all duration-500 ease-out;
}

/* Action buttons */
.fup-detail__actions {
  @apply flex gap-2 pt-1;
}

.fup-action {
  @apply px-3 py-2 rounded-md text-xs font-semibold
         cursor-pointer transition-all duration-150
         disabled:opacity-50 disabled:cursor-not-allowed
         flex items-center gap-1.5;
}
.fup-action--primary {
  @apply bg-n-blue-9 text-white hover:bg-n-blue-10;
}
.fup-action--secondary {
  @apply bg-n-slate-3 dark:bg-n-solid-3 text-n-slate-12
         hover:bg-n-slate-4 border border-n-weak;
}
.fup-action--danger {
  @apply text-n-ruby-11 hover:text-n-ruby-12
         hover:bg-n-ruby-2 dark:hover:bg-n-ruby-3
         border border-transparent hover:border-n-ruby-6;
}

/* Error detail (expanded) */
.fup-error-detail {
  @apply mx-4 mb-3 px-4 py-2.5 text-[11px] text-n-ruby-10
         bg-n-ruby-2 dark:bg-n-ruby-3 rounded-lg border border-n-ruby-6;
}
</style>
